import { eur, hrs } from "@/lib/apiClient";

const SOURCE_LABELS = {
  site_grade: "Site rate", site: "Site rate", grade: "Grade rate", employee: "Employee rate", employee_rate: "Employee rate",
  job_role: "Job role rate", customer: "Customer rate", job_type: "Job type rate", default: "Standard rate", salary: "Salary equivalent",
  salaried: "Salary equivalent", bank_holiday: "Bank holiday", leave: "Leave",
  employee_profile: "Employee rate", employee_standard: "Employee rate", site_specific_pay: "Site rate", salary_derived: "Salary equivalent",
  standard: "Standard rate", none: "No rate set", employee_site_specific: "Employee site rate", customer_grade: "Customer rate",
  customer_default: "Customer rate", employee_default: "Employee charge rate", site_standard: "Site rate", site_default: "Site rate", settings_default: "Standard rate", company_default: "Company rate",
};
const DAY = { saturday: "Saturday", sunday: "Sunday", bank_holiday: "Bank holiday" };

export const rateLabel = (src) => {
  if (!src) return "";
  const m = /^(?:policy|premium)_(\w+)$/.exec(src);
  if (m) return `${DAY[m[1]] || m[1]} rate`;
  return SOURCE_LABELS[src] || src.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
};

const bandLabel = (e, mult) => (e.premium_kind ? DAY[e.premium_kind] || "Premium" : mult === 1 ? "Basic" : "Overtime");
const tone = (e, mult) => (e.premium_kind === "bank_holiday" || e.bank_holiday ? "text-rose-700" : mult >= 2 ? "text-red-700" : mult > 1 ? "text-amber-700" : "text-zinc-600");
const r2 = (n) => Math.round(n * 100) / 100;
const close = (lines, total) => Math.abs(lines.reduce((s, l) => s + l.amount, 0) - (total || 0)) < 0.03;

export function splitBands(e) {
  if (e.pay_bands?.length) return e.pay_bands.filter((b) => b.minutes > 0).map((b) => ({ mult: Number(b.mult), minutes: b.minutes }));
  const ot15 = e.ot15_minutes ?? e.overtime_minutes ?? 0;
  const ot2 = e.ot2_minutes || 0;
  const basic = e.basic_minutes ?? Math.max(0, (e.paid_minutes || 0) - ot15 - ot2);
  const mult2 = e.premium_kind && e.premium_kind !== "saturday" ? Number(e.premium_multiplier || 2) : 2;
  return [{ mult: 1, minutes: basic }, { mult: 1.5, minutes: ot15 }, { mult: mult2, minutes: ot2 }].filter((b) => b.minutes > 0);
}

export function payLines(e) {
  if (e.kind === "leave") {
    if (!e.bank_holiday) return [];
    return [{ label: "Bank holiday (paid day off)", minutes: e.paid_minutes ?? (e.hours || 0) * 60, mult: 1, rate: e.pay_rate || 0, amount: e.labour_cost || 0, tone: "text-rose-700" }];
  }
  const base = e.base_pay_rate ?? e.pay_rate ?? 0;
  const noSplit = e.basic_minutes == null && e.ot15_minutes == null && !e.pay_bands?.length && /^(policy|premium)_/.test(e.pay_rate_source || "");
  const bands = noSplit ? [] : splitBands(e);
  const build = (otRate) => {
    const lowOt = Math.min(...bands.filter((b) => b.mult > 1).map((b) => b.mult));
    return bands.map((b) => {
      const rate = otRate && b.mult === lowOt ? otRate : base * b.mult;
      return { label: bandLabel(e, b.mult), minutes: b.minutes, mult: b.mult, rate, amount: r2((b.minutes / 60) * rate), tone: tone(e, b.mult) };
    });
  };
  for (const lines of [build(null), build(e.ot_rate)]) if (bands.length && close(lines, e.labour_cost)) return lines;
  return [{ label: rateLabel(e.pay_rate_source) || "Pay", minutes: e.paid_minutes || 0, mult: null, rate: e.pay_rate || 0, amount: e.labour_cost || 0, tone: "text-zinc-600" }];
}

export function chargeLines(e) {
  if (e.kind === "leave") return [];
  const paid = e.paid_minutes || 0;
  const cOt = Math.min(e.charge_ot_minutes || 0, paid);
  const lines = [
    { label: rateLabel(e.charge_rate_source) || "Charge", minutes: paid - cOt, mult: 1, rate: e.charge_rate || 0 },
    { label: "Overtime charge", minutes: cOt, mult: 1.5, rate: e.ot_charge_rate || 0 },
  ].filter((l) => l.minutes > 0).map((l) => ({ ...l, amount: r2((l.minutes / 60) * l.rate), tone: l.mult > 1 ? "text-amber-700" : "text-zinc-600" }));
  if (lines.length && close(lines, e.labour_charge)) return lines;
  if (!paid && !e.labour_charge) return [];
  return [{ label: rateLabel(e.charge_rate_source) || "Charge", minutes: paid, mult: null, rate: paid ? (e.labour_charge || 0) / (paid / 60) : 0, amount: e.labour_charge || 0, tone: "text-zinc-600" }];
}

function Line({ kind, l, tid }) {
  return (
    <div data-testid={tid} className={`grid grid-cols-[1fr_auto] gap-x-3 ${l.tone}`}>
      <span className="min-w-0"><span className="text-zinc-400">{kind}</span> · {l.label}: <span className="num">{hrs(l.minutes / 60)}{l.mult != null ? ` at ${l.mult}×` : ""} ({eur(l.rate)}/hr)</span></span>
      <span className="num text-right">{eur(l.amount)}</span>
    </div>
  );
}

// Admin: one line per pay band and charge band, then travel and employer costs.
export function RateLines({ e, tid }) {
  const pay = payLines(e);
  const charge = chargeLines(e);
  const travel = (e.travel_minutes || 0) > 0 || (e.travel_km || 0) > 0;
  return (
    <div data-testid={tid} className="mt-1 space-y-0.5 bg-zinc-50/70 rounded px-2 py-1.5">
      {pay.map((l, i) => <Line key={`p${i}`} kind="Pay" l={l} tid={`${tid}-pay-${i}`} />)}
      {pay.length > 1 && <div className="grid grid-cols-[1fr_auto] gap-x-3 font-semibold text-zinc-700" data-testid={`${tid}-pay-total`}><span>Pay total</span><span className="num">{eur(e.labour_cost)}</span></div>}
      {charge.map((l, i) => <Line key={`c${i}`} kind="Charge" l={l} tid={`${tid}-charge-${i}`} />)}
      {charge.length > 1 && <div className="grid grid-cols-[1fr_auto] gap-x-3 font-semibold text-zinc-700" data-testid={`${tid}-charge-total`}><span>Charge total</span><span className="num">{eur(e.labour_charge)}</span></div>}
      {travel && <div className="grid grid-cols-[1fr_auto] gap-x-3 text-sky-700" data-testid={`${tid}-travel`}><span><span className="text-zinc-400">Travel</span> · <span className="num">{hrs((e.travel_minutes || 0) / 60)}{e.travel_km ? ` · ${e.travel_km} km` : ""}</span></span><span className="num">{eur(e.travel_cost)}</span></div>}
      <div className="num text-zinc-500 pt-0.5 border-t border-zinc-100" data-testid={`${tid}-costs`}>PRSI {eur(e.prsi_cost)} · holiday {eur(e.holiday_cost)} · loaded cost {eur(e.employment_cost_total)}</div>
    </div>
  );
}

// Staff: hours at each rate, no money.
export function HoursSplit({ e, tid }) {
  const bands = splitBands(e);
  if (!bands.length || (bands.length === 1 && bands[0].mult === 1 && !e.premium_kind)) return null;
  return (
    <div data-testid={tid} className="flex flex-wrap gap-x-2 text-[11px] mt-0.5">
      {bands.map((b, i) => <span key={i} className={`num ${tone(e, b.mult)}`}>{bandLabel(e, b.mult)} {hrs(b.minutes / 60)} at {b.mult}×</span>)}
    </div>
  );
}
